import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Syringe, CheckCircle2, Circle, ChevronDown, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";

export default function Vaccinations({ patientId, hasDob }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [markDlg, setMarkDlg] = useState(null); // {vaccine}
  const [form, setForm] = useState({ given_date: "", lot: "", site: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/vaccinations/${patientId}`);
      setData(r.data);
    } catch (e) { console.debug("[silent]", e); }
    setLoading(false);
  };
  // Intentionally omit `load` from deps — it's a stable module-local function and adding
  // it would trigger a fetch loop when the component re-renders after setData/setLoading.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [patientId]);

  const openMark = (vaccine) => {
    setForm({ given_date: new Date().toISOString().slice(0, 10), lot: "", site: "Left deltoid" });
    setMarkDlg({ vaccine });
  };
  const confirmMark = async () => {
    if (!markDlg?.vaccine) return;
    try {
      await api.post(`/vaccinations/${patientId}/mark`, { vaccine: markDlg.vaccine, ...form });
      toast.success(`${markDlg.vaccine} marked given`);
      setMarkDlg(null);
      load();
    } catch { toast.error("Failed to save"); }
  };

  const unmark = async (vaccine) => {
    if (!confirm(`Unmark ${vaccine} as given?`)) return;
    try {
      await api.delete(`/vaccinations/${patientId}?vaccine=${encodeURIComponent(vaccine)}`);
      load();
    } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="text-xs text-slate-500">Loading schedule…</div>;
  if (!data) return null;
  if (!data.has_dob) {
    return <div className="text-xs text-slate-500 alert-info rounded-md px-3 py-2">Add the patient's date of birth to see age-based vaccination schedule.</div>;
  }

  const totalGiven = data.schedule.reduce((s, row) => s + row.vaccines.filter((v) => v.given).length, 0);
  const totalDue = data.schedule.reduce((s, row) => s + row.vaccines.filter((v) => v.status === "due" || v.status === "overdue").length, 0);
  const scheduleType = data.schedule_type || "pediatric";
  const isAdult = scheduleType === "adult";

  return (
    <div>
      <div className="flex items-center gap-3 mb-3 text-xs flex-wrap">
        <span
          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border font-semibold uppercase tracking-wider text-[10px] ${isAdult ? "bg-pink-50 text-pink-700 border-pink-200" : "bg-sky-50 text-sky-700 border-sky-200"}`}
          data-testid="vax-schedule-type"
        >
          <Syringe size={11} /> {isAdult ? "Adult schedule" : "Pediatric (IAP) schedule"}
          {data.age_years != null && <span className="opacity-70 normal-case tracking-normal">· age {data.age_years}</span>}
          {isAdult && data.has_risk_condition && <span className="opacity-70 normal-case tracking-normal">· high-risk conditions</span>}
        </span>
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"><CheckCircle2 size={12} /> {totalGiven} given</span>
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200"><Syringe size={12} /> {totalDue} due/overdue</span>
      </div>
      <div className="space-y-1">
        {data.schedule.map((row) => {
          const dueCount = row.vaccines.filter((v) => v.status === "due" || v.status === "overdue").length;
          const givenCount = row.vaccines.filter((v) => v.given).length;
          const isExp = expanded[row.age_label] ?? (dueCount > 0);
          const tone =
            row.vaccines.some((v) => v.status === "overdue") ? "border-red-200 bg-red-50/40"
            : row.vaccines.some((v) => v.status === "due") ? "border-amber-200 bg-amber-50/40"
            : "border-slate-200 bg-white";
          return (
            <div key={row.age_label} className={`border rounded-md ${tone}`} data-testid={`vax-row-${row.age_label.replace(/\s+/g, "-")}`}>
              <button
                onClick={() => setExpanded({ ...expanded, [row.age_label]: !isExp })}
                className="w-full flex items-center justify-between p-3 text-left"
              >
                <div className="flex items-center gap-2">
                  {isExp ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <div>
                    <div className="text-sm font-medium">{row.age_label}</div>
                    <div className="text-xs text-slate-500 tabular">
                      {row.due_date ? new Date(row.due_date).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : ""}
                    </div>
                  </div>
                </div>
                <div className="text-xs">
                  {givenCount === row.vaccines.length && (
                    <span className="text-emerald-700 font-semibold uppercase tracking-wider">Complete</span>
                  )}
                  {givenCount < row.vaccines.length && dueCount > 0 && (
                    <span className="text-red-700 font-semibold uppercase tracking-wider">{dueCount} due</span>
                  )}
                  {givenCount < row.vaccines.length && dueCount === 0 && (
                    <span className="text-slate-500 uppercase tracking-wider text-[10px]">{row.vaccines.length - givenCount} pending</span>
                  )}
                </div>
              </button>
              {isExp && (
                <div className="border-t border-slate-100 divide-y divide-slate-100">
                  {row.vaccines.map((v) => (
                    <div key={v.name} className="flex items-center justify-between px-4 py-2 text-sm">
                      <div className="flex items-center gap-2">
                        {v.given ? <CheckCircle2 size={14} className="text-emerald-600" /> : <Circle size={14} className="text-slate-300" />}
                        <span className={v.given ? "line-through text-slate-500" : ""}>{v.name}</span>
                        {v.given && v.given_date && (
                          <span className="text-[10px] text-slate-500 tabular">· {new Date(v.given_date).toLocaleDateString()}</span>
                        )}
                        {!v.given && v.status === "overdue" && (
                          <span className="text-[10px] text-red-600 font-semibold uppercase tracking-wider">overdue</span>
                        )}
                        {!v.given && v.status === "due" && (
                          <span className="text-[10px] text-amber-600 font-semibold uppercase tracking-wider">due</span>
                        )}
                      </div>
                      {v.given ? (
                        <button onClick={() => unmark(v.name)} className="text-xs text-slate-400 hover:text-red-600 px-2 py-1" data-testid={`vax-unmark-${v.name.replace(/[\s/]/g, "-")}`}>Unmark</button>
                      ) : (
                        <button onClick={() => openMark(v.name)} className="text-xs text-pink-600 hover:text-pink-800 font-medium px-2 py-1" data-testid={`vax-mark-${v.name.replace(/[\s/]/g, "-")}`}>Mark given</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!markDlg} onOpenChange={(v) => !v && setMarkDlg(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Syringe size={16} /> Mark vaccine given</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm"><span className="text-slate-500">Vaccine:</span> <span className="font-medium">{markDlg?.vaccine}</span></div>
            <div>
              <Label className="overline">Date given</Label>
              <Input type="date" value={form.given_date} onChange={(e) => setForm({ ...form, given_date: e.target.value })} className="tabular" data-testid="vax-date-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Batch / Lot</Label>
                <Input value={form.lot} onChange={(e) => setForm({ ...form, lot: e.target.value })} placeholder="Optional" data-testid="vax-lot-input" />
              </div>
              <div>
                <Label className="overline">Site</Label>
                <Input value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} data-testid="vax-site-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkDlg(null)}>Cancel</Button>
            <Button onClick={confirmMark} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="vax-confirm-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
